import { createGoogleTokenBindingApi2 } from '../../api/create-google-token-binding-v2';
import { TrustFrameError } from '../../errors';
import { ApiHost, BindGoogleTokenOptionsV2, PaymentFrameEventsV2 } from '../../types';
import { paymentForm } from '../payment-form';
import { GooglePayProvider } from './GooglePayProvider';
import { loadGooglePaySdk } from './loadGooglePaySdk';

const ALL_METHODS: google.payments.api.CardAuthMethod[] = [
    'PAN_ONLY' /** Just cards */,
    'CRYPTOGRAM_3DS' /** Cards bound to Google Wallet on Android */,
];

const ALL_NETWORKS: google.payments.api.CardNetwork[] = [
    'AMEX',
    'DISCOVER',
    'JCB',
    'MASTERCARD',
    'VISA',
    'INTERAC',

    // Intentionally skipped (not supported by testing and/or country-specific):
    // 'ELECTRON',
    // 'MAESTRO',
    // 'ELO',
    // 'ELO_DEBIT',
];

export const bindGoogleTokenV2 = async (apiHost: ApiHost, options: BindGoogleTokenOptionsV2) => {
    const [url] = await Promise.all([createGoogleTokenBindingApi2(apiHost, options), loadGooglePaySdk()]);

    const targetOrigin = new URL(url).origin;

    /**
     * Payment data request, that will be used for binding.
     * Merchant id and public key would be filled later in the binding process.
     *
     * @see https://developers.google.com/pay/api/web/reference/request-objects#PaymentDataRequest
     */
    const request: google.payments.api.PaymentDataRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,

        merchantInfo: {
            merchantId: '$merchantId',
            merchantName: options.businessName,
        },

        transactionInfo: {
            currencyCode: options.currency,
            countryCode: options.countryCode,

            totalPrice: options.amount,
            totalPriceStatus: 'FINAL',
        },

        allowedPaymentMethods: [
            {
                type: 'CARD',
                parameters: {
                    allowedAuthMethods: ALL_METHODS,
                    allowedCardNetworks: ALL_NETWORKS,
                },
                tokenizationSpecification: {
                    type: 'DIRECT',
                    parameters: {
                        protocolVersion: 'ECv2',
                        publicKey: '$publicKey',
                    },
                },
            },
        ],

        callbackIntents: ['PAYMENT_AUTHORIZATION'],
    };

    let provider: GooglePayProvider | null = null;

    const onDomMount = (frame: HTMLIFrameElement) => {
        if (!frame.contentWindow) {
            throw new TrustFrameError('Unable to attach GooglePayProvider to form frame');
        }

        provider = new GooglePayProvider(frame.contentWindow, targetOrigin, request);
        provider.start();
    };

    const onDomUnmount = () => {
        if (provider) {
            provider.stop();
            provider = null;
        }
    };

    return paymentForm<PaymentFrameEventsV2>(apiHost, { url, onDomMount, onDomUnmount });
};
